import type { DateTime } from 'luxon'
import { getMyDemoInvitations } from '../firestore/Invitation'
import {
  createDemoRoomState,
  getMyDemoRoomStates,
  startOwnDemoMeeting,
  updateRoomState,
} from '../firestore/RoomState'
import { getExternalDemoSlideDecks } from '../firestore/SlideDeck'
import { BaseDemosCubit } from './BaseDemosCubit'
import { RoomStateDemoType } from '../firestore/RoomState/types'

/**
 * demos dashboard for externalCanDemo users: scoped to their own demos,
 * curated deck catalog, always regular demos led by the current user.
 * Firestore rules only permit these self-scoped operations, so this
 * cubit has no path to the org-wide queries.
 */
export class ExternalDemosCubit extends BaseDemosCubit {
  protected roomStatesStream() {
    return getMyDemoRoomStates(this.repository)
  }

  protected slideDecksStream() {
    return getExternalDemoSlideDecks(this.repository)
  }

  protected invitationsStream(roomId: string) {
    return getMyDemoInvitations(this.repository, { roomId })
  }

  protected startMeetingWrite(roomStateId: string) {
    return startOwnDemoMeeting(this.repository, { roomStateId })
  }

  async createDemoMeeting({
    slideDeckId,
    scheduledAt,
    roomStateName,
  }: {
    slideDeckId: string
    scheduledAt: DateTime
    roomStateName?: string
  }) {
    const result = await createDemoRoomState(this.repository, {
      userId: this.repository.uid,
      slideDeckId,
      scheduledAt,
      demoType: RoomStateDemoType.regular,
      name: roomStateName,
    })

    return result.id
  }

  async updateDemoMeeting(
    roomStateId: string,
    {
      slideDeckId,
      scheduledAt,
      roomStateName,
    }: {
      slideDeckId: string
      scheduledAt: DateTime
      roomStateName?: string
    }
  ) {
    // only the fields externalDemoOwnerUpdate() permits in rules
    await updateRoomState(this.repository.firestore, roomStateId, {
      slideDeckId,
      scheduledAt: scheduledAt.toJSDate(),
      roomStateName: roomStateName ?? '',
    })
  }
}
